/**
 * Smart control / protocol tile rules for раздвижные configurator.
 * Tiles stay visible when disabled (no layout jump); variant matrix is final gate.
 */
import type { ProtocolGroup } from './configuratorSchema';

const norm = (s: string | null | undefined) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е');

export const WIRELESS_PROTOCOLS = ['Wi-Fi (TUYA)', 'ZigBee', 'Matter'] as const;

export const DEPRECATED_PROTOCOLS = ['Wi-Fi (MiHome)'] as const;

export const PHYSICAL_CONTROLS = ['Фаза', 'RS-485', 'Сухой контакт', 'Пульт ДУ', 'RF'] as const;

export type ControlSelection = {
  protocol: string | null;
  controlTags: string[];
};

export function isWirelessProtocol(value: string | null | undefined): boolean {
  if (!value) return false;
  const n = norm(value);
  return WIRELESS_PROTOCOLS.some((p) => norm(p) === n);
}

/** Пульт/фаза из UI — не требуем в control_types у ZigBee/Wi-Fi строк без физ. тегов в прайсе. */
export function isBundledPhysicalControlTag(tag: string | null | undefined): boolean {
  const tn = norm(tag);
  if (!tn) return false;
  return PHYSICAL_CONTROLS.some((p) => {
    const pn = norm(p);
    return tn === pn || tn.includes(pn) || pn.includes(tn);
  });
}

/**
 * Logical exclusion — only between wireless protocols (one chip).
 * Physical tags (Фаза, RS-485, пульт…) are gated by the variant matrix only:
 * e.g. MR-2234F5 in прайсе has «Фаза;RS-485» together.
 */
export function controlIncompatReason(
  sel: ControlSelection,
  tile: { kind: 'protocol' | 'control'; value: string },
): string | null {
  if (tile.kind !== 'protocol') return null;

  const proto = sel.protocol;
  const v = tile.value;
  const vn = norm(v);

  if (proto && norm(proto) !== vn && isWirelessProtocol(proto) && isWirelessProtocol(v)) {
    return `Несовместимо с ${proto}`;
  }
  if (vn.includes('wifi') || vn.includes('tuya') || vn.includes('mihome')) {
    if (norm(proto) === 'zigbee') return 'Несовместимо с ZigBee';
    if (norm(proto) === 'matter') return 'Несовместимо с Matter';
  }
  if (vn === 'zigbee') {
    if (norm(proto).includes('wifi') || norm(proto).includes('tuya')) return 'Несовместимо с Wi-Fi';
    if (norm(proto) === 'matter') return 'Несовместимо с Matter';
  }
  if (vn === 'matter') {
    if (norm(proto).includes('wifi') || norm(proto).includes('tuya')) return 'Несовместимо с Wi-Fi';
    if (norm(proto) === 'zigbee') return 'Несовместимо с ZigBee';
  }
  return null;
}

/** After picking a wireless protocol — suggest bundled controls (e.g. пульт with Wi-Fi). */
export function autoControlTagsForProtocol(protocol: string | null): string[] {
  if (!protocol) return [];
  const n = norm(protocol);
  if (n.includes('wifi') || n.includes('tuya') || n === 'zigbee' || n === 'matter') {
    return ['Пульт ДУ'];
  }
  return [];
}

export function protocolGroupLabel(pg: ProtocolGroup): string {
  if (pg.label) return pg.label;
  if (pg.id === 'smart_home' || pg.id === 'wireless') return 'Беспроводные протоколы';
  if (pg.id === 'wired' || pg.id === 'physical') return 'Физические интерфейсы';
  return pg.exclusive ? 'Протокол (один)' : 'Подключение';
}
